import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Buttons/Button';
import Input from 'src/components/FormComponents/Input';
import { DialogBody, DialogFooter } from 'src/components/Modals/FormDialog';
import container from 'src/container';
import {
    AccessSession,
    accessErrorCode,
    accessErrorMessage,
    formatContact,
    formatDuration,
} from './accessErrors';

const {
    cradle: { authService },
} = container;

/** SMS is limited to one code an hour per number, so its resend waits that long. */
const resendCooldownMs = (session: AccessSession) =>
    session.contact.kind === 'phone' ? 60 * 60_000 : 60_000;

type CodeStepProps = {
    session: AccessSession;
    submitLabel: string;
    onSubmit: (code: string) => Promise<void>;
    /** Uses the signed-in resend endpoint for contact changes. */
    settings?: boolean;
    /** In a `FormDialog`: body and footer with Cancel next to the submit button. */
    onCancel?: () => void;
    children?: ReactNode;
};

/** Six-digit code entry with resend cooldown for activation and contact changes. */
export const CodeStep = ({
    session,
    submitLabel,
    onSubmit,
    settings,
    onCancel,
    children,
}: CodeStepProps) => {
    const { t } = useTranslation();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [deliveryFailed, setDeliveryFailed] = useState(
        Boolean(session.deliveryFailed),
    );
    const [resendAt, setResendAt] = useState(
        () => Date.now() + resendCooldownMs(session),
    );
    const seconds = useSecondsUntil(resendAt);
    const contact = formatContact(session.contact);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (busy || !/^\d{6}$/.test(code)) return;
        setBusy(true);
        setError(undefined);
        try {
            await onSubmit(code);
        } catch (cause) {
            if (accessErrorCode(cause)?.startsWith('CODE_')) setCode('');
            setError(accessErrorMessage(cause, t));
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        setBusy(true);
        setError(undefined);
        try {
            await authService.accountAccess(
                settings ? 'contacts/resend' : 'resend',
                { session: session.session },
                settings,
            );
            setCode('');
            setDeliveryFailed(false);
            setResendAt(Date.now() + resendCooldownMs(session));
        } catch (cause) {
            const retryAfter = (cause as { retryAfter?: number }).retryAfter;
            if (retryAfter) setResendAt(Date.now() + retryAfter * 1000);
            else setDeliveryFailed(true);
            setError(accessErrorMessage(cause, t));
        } finally {
            setBusy(false);
        }
    };

    const resendButton = (
        <button
            type="button"
            className="text-sm underline underline-offset-2 disabled:no-underline disabled:text-mongooseGray"
            disabled={busy || seconds > 0}
            onClick={() => void resend()}
        >
            {seconds > 0
                ? t('accountAccess.resendIn', {
                      time:
                          seconds > 90
                              ? formatDuration(seconds, t)
                              : `${seconds} s`,
                  })
                : t('accountAccess.resend')}
        </button>
    );

    const sentNote = (
        <p
            className="text-sm text-mongooseGray"
            role={deliveryFailed ? 'alert' : undefined}
        >
            {deliveryFailed
                ? t('accountAccess.deliveryFailed', { contact })
                : t('accountAccess.codeSentTo', {
                      contact,
                      duration: formatDuration(session.expiresIn ?? 600, t),
                  })}
        </p>
    );
    const codeInput = (
        <Input
            className="text-input tracking-[0.5em]"
            label={t('accountAccess.codeLabel')}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            value={code}
            disabled={busy}
            error={error ? { type: 'server', message: error } : undefined}
            onChange={event =>
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
            }
        />
    );

    if (onCancel)
        return (
            <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
                <DialogBody>
                    {sentNote}
                    {codeInput}
                </DialogBody>
                <DialogFooter start={resendButton}>
                    <Button
                        title={t('common.cancel')}
                        onClick={onCancel}
                        size="comfortable"
                    />
                    <Button
                        submit
                        active
                        size="comfortable"
                        title={submitLabel}
                        disabled={busy || code.length !== 6}
                    />
                </DialogFooter>
            </form>
        );

    return (
        <form className="flex flex-col gap-4" onSubmit={submit}>
            {sentNote}
            {codeInput}
            <div className="flex justify-center">
                <Button
                    submit
                    title={submitLabel}
                    disabled={busy || code.length !== 6}
                />
            </div>
            <div className="flex flex-col items-start gap-2 text-sm">
                {resendButton}
                {children}
            </div>
        </form>
    );
};

const useSecondsUntil = (timestamp: number): number => {
    const remaining = () =>
        Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
    const [seconds, setSeconds] = useState(remaining);
    useEffect(() => {
        setSeconds(remaining());
        const timer = setInterval(() => {
            const next = remaining();
            setSeconds(next);
            if (next === 0) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timestamp]);
    return seconds;
};
